import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { GET_CARRIER_BUSINESS_NAME, RestService } from 'cargo-chief-commons';
import { UrlService } from '@service/url/url.service';

// CN-8645 (F7): resolves carrier business names by id, cached for the session.
// The Notes and Chat tabs only carry a carrierId on each entry, and a load's
// conversation spans previous runs whose carriers are not in the current bid
// list, so names are resolved on demand via the carriers-by-ids endpoint and
// shared across both tabs through one cache.
@Injectable({ providedIn: 'root' })
export class CarrierNameService {
	private readonly cache = new Map<string, string>();

	constructor(
		private readonly restService: RestService,
		private readonly urlService: UrlService
	) {}

	// Resolve names for the given ids, fetching only those not already cached.
	// Emits a snapshot map (id -> business name) covering every requested id we
	// know a name for. Never errors the caller: on failure it returns the
	// already-cached subset so the UI degrades to its fallback label.
	resolveNames(ids: Array<string | null | undefined>): Observable<Map<string, string>> {
		const distinct = Array.from(
			new Set(ids.filter((id): id is string => !!id).map((id) => String(id)))
		);
		const missing = distinct.filter((id) => !this.cache.has(id));

		if (!missing.length) {
			return of(this.snapshot(distinct));
		}

		return this.restService.POST(this.urlService.getCarriersByIds(), { carrierIDs: missing }).pipe(
			map((result: any) => {
				const carriers = result?.data?.content || [];
				const returned = new Set<string>();
				carriers.forEach((carrier: any) => {
					const id = String(carrier.id);
					returned.add(id);
					this.cache.set(
						id,
						GET_CARRIER_BUSINESS_NAME(carrier?.businessName, carrier?.doingBusinessAs)
					);
				});
				// Cache an empty sentinel for ids we asked for but got nothing back, so an
				// unresolvable carrier isn't re-fetched on every emails$ / note-page cycle.
				// snapshot() filters falsy names, so sentinels never surface in the output.
				missing.forEach((id) => {
					if (!returned.has(id)) {
						this.cache.set(id, '');
					}
				});
				return this.snapshot(distinct);
			}),
			catchError(() => of(this.snapshot(distinct)))
		);
	}

	private snapshot(ids: string[]): Map<string, string> {
		const out = new Map<string, string>();
		ids.forEach((id) => {
			const name = this.cache.get(id);
			if (name) {
				out.set(id, name);
			}
		});
		return out;
	}
}
